import { Component, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
  XCircle,
} from 'lucide-react'
import { cn } from './internal/utils'
import { RADIUS, STATUS_CLASSES } from './internal/presets'

const DEFAULT_ICONS = {
  success: CheckCircle2,
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
}

const TYPE_TO_STATUS = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
}

/**
 * antd Alert. Supports type, message, description, showIcon, closable, action,
 * banner and the controlled/uncontrolled close behaviour.
 */
const Alert = ({
  type = 'info',
  message,
  description,
  showIcon = false,
  closable = false,
  closeIcon,
  closeText,
  onClose,
  afterClose,
  action,
  icon,
  banner = false,
  className,
  style,
  ...rest
}) => {
  const [closed, setClosed] = useState(false)
  const resolvedType = banner && !type ? 'warning' : type
  const DefaultIcon = DEFAULT_ICONS[resolvedType] || Info

  const handleClose = (event) => {
    setClosed(true)
    onClose?.(event)
    afterClose?.()
  }

  if (closed) return null

  return (
    <div
      role="alert"
      className={cn(
        'ui-alert flex items-start gap-2 border px-3 py-2',
        `ui-alert-${resolvedType}`,
        banner ? 'rounded-none border-x-0 border-t-0' : RADIUS,
        STATUS_CLASSES[TYPE_TO_STATUS[resolvedType]] || STATUS_CLASSES.info,
        className
      )}
      style={style}
      {...rest}
    >
      {showIcon ? (
        <span className="ui-alert-icon mt-0.5 shrink-0" aria-hidden="true">
          {icon ?? <DefaultIcon size={description ? 20 : 16} />}
        </span>
      ) : null}

      <div className="ui-alert-content min-w-0 flex-1">
        {message ? (
          <div
            className={cn(
              'ui-alert-message',
              description ? 'mb-1 font-semibold' : 'text-sm font-medium'
            )}
          >
            {message}
          </div>
        ) : null}
        {description ? (
          <div className="ui-alert-description text-sm opacity-90">{description}</div>
        ) : null}
      </div>

      {action ? <div className="ui-alert-action shrink-0">{action}</div> : null}

      {closable || closeText ? (
        <button
          type="button"
          aria-label="Fermer"
          onClick={handleClose}
          className="ui-alert-close-icon shrink-0 rounded p-1 opacity-60 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
        >
          {closeText ?? closeIcon ?? <X size={14} aria-hidden="true" />}
        </button>
      ) : null}
    </div>
  )
}

/** antd exposes a real error boundary on Alert. */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null, info: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    this.setState({ error, info })
  }

  render() {
    const { message, description, children } = this.props
    const { error, info } = this.state
    if (!error) return children
    return (
      <Alert
        type="error"
        showIcon
        message={message ?? String(error)}
        description={description ?? info?.componentStack}
      />
    )
  }
}

Alert.ErrorBoundary = ErrorBoundary

export { ErrorBoundary }
export default Alert
